import React , { useState, useEffect } from 'react';
import { List , Row, Col, InputNumber, Tooltip, Icon, Button, Badge, Radio, Select} from "antd";
import ImpressionManagementHolder from "../newSrc/features/campaignManager/styles/ImpressionManagementStyle"
import AdViewComponent from './AdViewComponent';
import { useTranslation } from 'react-i18next';
import { calculateImpressions, contentDisplayIsSuspended, getRelationShowDays, getSpotsPerDay, getMaxExtractableSpots } from "../services/display";
import { calculateValueForPrice } from "../services/utils";
import DisplayViewComponent from './DisplayViewComponent';
import Colors from '../themes/Colors';

const ImpressionManagementComponent = (props) => {

  const { t } = useTranslation();
  const { currentCampaign,
          reportSingleData,
          customContents,
          transferModalVisible,
          cart,
          showTransferModal,
          updateImpressionsDisplay,
          // creditAvailableOnTransferredImpressions, // Save impresions in transfer when close modal
      } = props;
  const DECIMAL = 8; // Is necessary for not lose precision in the calculations when the cmp are more diferent
  const DECIMAL_FOR_CHECK = 4;
  const [contentsDisplaysSelect, setContentsDisplaysSelect] = useState([]);
  const [customContentsAux, setCustomContentsAux] = useState(customContents);

  const [view, setView] = useState("ad"); // "ad" or "display"
  const [displayOptions, setDisplayOptions] = useState(cart);
  const [displayFilter, setDisplayFilter] = useState(["all"]); // List of display selected, "all" for all displays
  const [creditAvailable, setCreditAvailable] = useState(0);

  const [adVersions, setAdVersions] = useState([]);
  const [originVersion, setOriginVersion] = useState(undefined);
  const [destinationVersion, setDestinationVersion] = useState(undefined);
  const [originScreen, setOriginScreen] = useState(undefined);
  const [destinationScreen, setDestinationScreen] = useState(undefined);
  const [transferPercentage, setTransferPercentage] = useState(100);
  
  const format = new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: currentCampaign.currency,
    minimumFractionDigits: 3,
  })

  const handleRadioChange = (e) => {
    setView(e.target.value);
  }

  const handleTransfer = () => {
    if (!transferPercentage) return;

    const percentage = transferPercentage / 100;
    const impressionChanges = new Map();
    const allOriginContents = [];
    const allDestinationContents = [];

    

    // Get all origin and destination contents
    contentsDisplaysSelect.forEach(group => {
      group.customContents.forEach(item => {
        if (view === 'ad' && item.content && item.content.content_version_name === originVersion) {
          allOriginContents.push(item);
        }
        // Exclude SUSPENDED items from destinations
        if (view === 'ad' && item.content && item.content.content_version_name === destinationVersion && !contentDisplayIsSuspended(item)) {
          allDestinationContents.push(item);
        }
        if (view === 'display' && item.displayId === originScreen) {
          allOriginContents.push(item);
        }
        // Exclude SUSPENDED items from destinations
        if (view === 'display' && item.displayId === destinationScreen && !contentDisplayIsSuspended(item)) {
          allDestinationContents.push(item);
        }
      });
    });

    if (allOriginContents.length > 0 && allDestinationContents.length > 0) {
      let totalCreditToTransfer = 0;

      // Group origin contents by displayId to calculate limits per display
      const originsByDisplay = allOriginContents.reduce((acc, origin) => {
        if (!acc[origin.displayId]) acc[origin.displayId] = [];
        acc[origin.displayId].push(origin);
        return acc;
      }, {});

      // 1. Calculate credit to transfer from origins, respecting display-level limits
      Object.entries(originsByDisplay).forEach(([displayId, displayOrigins]) => {
        // Get display-level discount info from first content (same for all contents in display)
        const firstContent = displayOrigins[0];
        const appliedDiscount = firstContent.appliedDiscount || null;
        const relationShowDays = firstContent.relationShowDays || 0;
        
        // Check if any origin has revision_status "out_of_service" or "rejected"
        const hasExcludedStatus = displayOrigins.some(origin => 
          origin.revision_status === "out_of_service" || origin.revision_status === "rejected"
        );
        
        // Calculate current total spots for this display
        const currentDisplayTotal = displayOrigins.reduce((total, origin) => {
          const value = origin.promised_shows_update !== undefined ? origin.promised_shows_update : (origin.promised_shows || 0);
          return total + value;
        }, 0);
        
        // Calculate the minimum spots to keep for this display (absolute minimum based on discount)
        // This is the floor that we cannot go below
        let minSpotsToKeep = 0;
        if (!hasExcludedStatus && appliedDiscount && appliedDiscount.min_impressions && relationShowDays > 0) {
          minSpotsToKeep = Math.ceil(appliedDiscount.min_impressions * relationShowDays);
        }
        
        // Calculate max extractable: current total minus the minimum we must keep
        const displayMaxExtractable = Math.max(0, currentDisplayTotal - minSpotsToKeep);
        
        // Track how much we've extracted from this display
        let displayExtracted = 0;
        
        displayOrigins.forEach(origin => {
          const currentValue = origin.promised_shows_update !== undefined ? origin.promised_shows_update : (origin.promised_shows || 0);
          
          // Calculate desired extraction based on percentage
          let desiredExtraction = Math.floor(currentValue * percentage);
          
          // Limit extraction to not exceed display's max extractable
          const remainingExtractable = Math.max(0, displayMaxExtractable - displayExtracted);
          const impressionsToReduce = Math.min(desiredExtraction, remainingExtractable, currentValue);
          
          // Update display extracted counter
          displayExtracted += impressionsToReduce;
          
          if (impressionsToReduce > 0 && origin.smart_campaign_cpm > 0) {
            const creditToTransfer = calculateValueForPrice(origin.smart_campaign_cpm, impressionsToReduce);
            totalCreditToTransfer += creditToTransfer;
          }

          const newOriginImpressions = currentValue - impressionsToReduce;
          impressionChanges.set(origin.relationId, isNaN(newOriginImpressions) ? 0 : newOriginImpressions);
        });
      });

      // 2. Distribute credit to destinations and calculate new impressions
      if (totalCreditToTransfer > 0) {
        const creditPerDestination = totalCreditToTransfer / allDestinationContents.length;

        allDestinationContents.forEach(dest => {
          const currentValue = dest.promised_shows_update !== undefined ? dest.promised_shows_update : (dest.promised_shows || 0);
          let impressionsToAdd = 0;
          if (dest.smart_campaign_cpm > 0) {
            impressionsToAdd = Math.floor(creditPerDestination / (dest.smart_campaign_cpm / 1000));
          }
          const newDestinationImpressions = currentValue + impressionsToAdd;
          
          // Use existing value in map if it exists (can happen if an item is both origin and destination)
          const existingValue = impressionChanges.get(dest.relationId) || 0;
          const finalValue = (existingValue > 0 && dest.content.content_version_name === originVersion) ? existingValue + impressionsToAdd : newDestinationImpressions;

          impressionChanges.set(dest.relationId, isNaN(finalValue) ? 0 : finalValue);
        });
      }
    }

    // Update contentsDisplaysSelect with new impression values
    const updatedContentsDisplaysSelect = contentsDisplaysSelect.map(group => ({
      ...group,
      customContents: group.customContents.map(item => {
        if (impressionChanges.has(item.relationId)) {
          return {
            ...item,
            promised_shows_update: impressionChanges.get(item.relationId),
            promised_shows_origin: item.promised_shows_origin !== undefined ? item.promised_shows_origin : item.promised_shows
          };
        }
        return item;
      }),
    }));

    setContentsDisplaysSelect(updatedContentsDisplaysSelect);
    refreshCustomContentsAux(updatedContentsDisplaysSelect);
  };

  const handleChangeDisplayFilter = (value) => {
    const prevValue = displayFilter;

    // Logic for remove or add "all" from the value
    if(value.includes("all") && value.length > 1){
      if(prevValue.includes("all")){
        const newValue = value.filter(item => item !== "all");
        setDisplayFilter(newValue);
        return;
      }

      if(!prevValue.includes("all")){
        setDisplayFilter(["all"]);
        return;
      }
    }

    // If the value is empty, set the display filter to "all"
    if (value.length === 0) {
      setDisplayFilter(["all"]);
      return;
    }

    setDisplayFilter(value);
  }

  const handleSearchDisplayFilter = (value) => {
    if(value === ""){
      setDisplayOptions(cart);
      return;
    }
    const filteredCart = cart.filter(item => item.name.toLowerCase().includes(value.toLowerCase()));
    setDisplayOptions(filteredCart);
  }

  const groupByCompany = (displayList) => {

    let result = Object.values(
      displayList.reduce((acc, display) => {
        const company_id = display.company_id || "Not assigned";
        if (!acc[company_id]) {
          acc[company_id] = {
            key: display.company.name,
            companyId: company_id,
            displays: [],
          };
        }

        acc[company_id].displays.push(display);

        return acc;
      }, {})
    );

    return result;
  };

  const groupViewData = (displayList) => {
    const groupedDisplays = groupByCompany(displayList);
    const viewData = [];

    groupedDisplays.forEach(group => {
      let viewDataGroup = {
        companyId: group.companyId,
        key: group.key,
        customContents: []
      };

      let displayIdList = displayList.filter(display => display.company_id === group.companyId).map(display => display.id);
      let filteredContents = customContentsAux.filter(item => displayIdList.includes(item.displayId));

      // add smart_campaign_cpm, discount info and min spots calculation
      const updatedContents = filteredContents.map(item => {
        
        const matchingDisplay = currentCampaign.contents_displays.find(d => d.display_id === item.displayId && d.cpm) || 
                              currentCampaign.contents_displays.find(d => d.display_id === item.displayId);

        // Get the applied discount from the relation
        const appliedDiscount = item.programmatic_volume_discount_apply || null;

        // Calculate spots per day and relation show days for this content
        const relationShowDays = getRelationShowDays(currentCampaign.start_date, currentCampaign.end_date, item.rules);
        // Calculate consumed impressions to use for spotsPerDay calculation
        const consumedImpressions = calculateImpressionsView(item);
        const spotsPerDay = getSpotsPerDay({...item, promised_shows: consumedImpressions}, currentCampaign.start_date, currentCampaign.end_date);

        return {
          ...item,
          display_name: matchingDisplay.display_name,
          smart_campaign_cpm: matchingDisplay.cpm || 0,
          content_version_name: item.content.content_version_name || "V1",
          appliedDiscount: appliedDiscount,
          spotsPerDay: spotsPerDay,
          relationShowDays: relationShowDays,
        }; 
      });

      viewDataGroup.customContents = updatedContents;
      viewData.push(viewDataGroup);
    })

    return viewData;
  }

  useEffect(() => {
    let displayList = cart;
    if (displayFilter.includes("all")) {
      displayList = cart;
    } else {
      displayList = cart.filter(item => displayFilter.includes(item.id));
    }

    const viewData = groupViewData(displayList);

    // Get versions
    const versions = new Set();
    customContentsAux.forEach(cc => {
      if (cc.content.content_version_name) {
        versions.add(cc.content.content_version_name);
      }
    });
    setAdVersions(Array.from(versions));

    setContentsDisplaysSelect(viewData);
  }, [transferModalVisible, view, displayFilter, cart, customContentsAux]);


  const refreshCustomContentsAux = (contentsDisplaysSelect) => {
    const newCustomContents = [];

    contentsDisplaysSelect.forEach(obj => {
      if (obj.customContents && Array.isArray(obj.customContents)) {
        newCustomContents.push(...obj.customContents);
      }
    });
    
    const updatedCustomContents = customContentsAux.map(item => {
      if (newCustomContents.some(cc => cc.relationId === item.relationId)) {
        return newCustomContents.find(cc => cc.relationId === item.relationId);
      }
      return item
    });

    setCustomContentsAux(updatedCustomContents);
  };

  const setCreditAvailableFormat = (value) => {
    const factor = Math.pow(10, DECIMAL);
    const valueAux = Math.floor(value * factor) / factor;
    setCreditAvailable(valueAux);
  }

  function customRound(value) {
    if (value % 1 >= 0.9) {
      return Math.ceil(value);
    } else {
      return Math.floor(value);
    }
  }

  // Update promised_shows_update in contentsDisplaysSelectAux and update creditAvailable
  const updateImpressions = (value, relationId) => {
    const valueAux = parseInt(value, 10)
    let contentsDisplaysSelectAux = contentsDisplaysSelect.map(group => {
      // Map through customContents within each group
      const updatedCustomContents = group.customContents.map(item => {
        if (item.relationId === relationId) {
          let promised_shows_update = parseInt((item.promised_shows_update === 0 ||
            item.promised_shows_update) ? item.promised_shows_update : calculateImpressionsView(item))

          //  Add creditAvailable
          if ((valueAux > promised_shows_update) && creditAvailable > 0) {
            let diff = valueAux - promised_shows_update
            let credit = calculateValueForPrice(item.smart_campaign_cpm, diff)
            setCreditAvailableFormat(creditAvailable - credit)
          }

          // Subtract creditAvailable
          if (valueAux < promised_shows_update ) {
            let diff = parseInt(promised_shows_update) - parseInt(valueAux)
            let credit = calculateValueForPrice(item.smart_campaign_cpm, diff)
            if(credit > 0) setCreditAvailableFormat(creditAvailable + credit)
          }
          return {
            ...item,
            promised_shows_update: valueAux,
            promised_shows_origin: item.promised_shows_origin || calculateImpressionsView(item)
          };
        }
        return item;
      });

      return {
        ...group,
        customContents: updatedCustomContents
      };
    });

    
    setContentsDisplaysSelect(contentsDisplaysSelectAux);
    refreshCustomContentsAux(contentsDisplaysSelectAux);
  };

  // Check if there is credit available is enough for the user to continue distributing
  const checkCreditAvailable = () => {
      return contentsDisplaysSelect.some(group => 
        group.customContents.some(item => {
          const cpm = parseFloat((item.smart_campaign_cpm / 1000).toFixed(DECIMAL_FOR_CHECK))
          const creditAvailableFormatted = parseFloat(creditAvailable.toFixed(DECIMAL_FOR_CHECK))
          return cpm != 0 ? cpm <= creditAvailableFormatted : false
        })
      );
  }

  // Update promised_shows to max value
  const updateImpressionsToMax = (relationId) => {
    let contentsDisplaysSelectAux = contentsDisplaysSelect.map(group => {
      const updatedCustomContents = group.customContents.map(item => {
        if (item.relationId === relationId) {
          const costPerImpression = parseFloat((item.smart_campaign_cpm / 1000).toFixed(DECIMAL_FOR_CHECK));
          const maxImpressions = Math.floor(creditAvailable / costPerImpression);
          if (maxImpressions < 1) return item; // Check if there is credit available

          let promised_shows_update = parseInt((item.promised_shows_update === 0 ||
            item.promised_shows_update) ? item.promised_shows_update : calculateImpressionsView(item))

          //  Add creditAvailable
          const creditUsed = maxImpressions * costPerImpression;
          let sum = maxImpressions + parseInt(promised_shows_update)
          const restCredit = creditAvailable - creditUsed
          setCreditAvailableFormat(restCredit)

          return {
            ...item,
            promised_shows_update: sum,
            promised_shows_origin: item.promised_shows_origin || calculateImpressionsView(item)
          };
        }
        return item;
      });

      return {
        ...group,
        customContents: updatedCustomContents
      };
    });
    setContentsDisplaysSelect(contentsDisplaysSelectAux);
    refreshCustomContentsAux(contentsDisplaysSelectAux);
  };

  const calculateImpressionsView = (relation) => {
    if(relation.length < 1) return 0
    if (!relation.content ){
      return relation.promised_shows
    }
    let impressions = calculateImpressions("to_consumed_not_subsidized" , relation, reportSingleData, {id: relation.displayId})
    return impressions
  }

  const calculateBudget = (origin, display) => {
    let impression =
      (origin == "origin") || (origin == "no_origin" && display.promised_shows_update === undefined) ?
      calculateImpressionsView(display) :
      display.promised_shows_update
    let calculate = calculateValueForPrice(display.smart_campaign_cpm, impression)
    return calculate.toFixed(2)
  }

  const updateBudget = (newBudgetValue, relationId) => {
    // Find item in nested structure
    let item = null;
    for (const group of contentsDisplaysSelect) {
      const foundItem = group.customContents.find(i => i.relationId === relationId);
      if (foundItem) {
        item = foundItem;
        break;
      }
    }
    
    if (!item) return;

    const cpm = item.smart_campaign_cpm;
    if (!cpm || cpm === 0) return;

    // Ensure newBudgetValue is a number
    const numericBudgetValue = typeof newBudgetValue === 'string' ? parseFloat(newBudgetValue.replace(/[^\d,.-]/g, '').replace(',', '.')) : newBudgetValue;

    if (isNaN(numericBudgetValue)) return;

    const newImpressions = Math.floor(numericBudgetValue / (cpm / 1000));

    updateImpressions(newImpressions, relationId);
  }

  // Calculate the minimum spots allowed for a specific record
  // This considers the display-level minimum required to maintain the discount
  const getMinSpotsForRecord = (record) => {
    // If record has excluded status, no minimum restriction
    if (record.revision_status === "out_of_service" || record.revision_status === "rejected") {
      return 0;
    }
    
    // If no discount applied, no minimum restriction
    if (!record.appliedDiscount || !record.appliedDiscount.min_impressions) {
      return 0;
    }
    
    const relationShowDays = record.relationShowDays || 0;
    if (relationShowDays <= 0) {
      return 0;
    }
    
    // Calculate the minimum spots to keep for the entire display
    const minSpotsToKeepForDisplay = Math.ceil(record.appliedDiscount.min_impressions * relationShowDays);
    
    // Get all contents for the same display
    let allDisplayContents = [];
    for (const group of contentsDisplaysSelect) {
      const displayContents = group.customContents.filter(c => c.displayId === record.displayId);
      allDisplayContents = allDisplayContents.concat(displayContents);
    }
    
    // Calculate current total for the display (excluding the current record)
    const otherContentsTotal = allDisplayContents
      .filter(c => c.relationId !== record.relationId)
      .reduce((total, c) => {
        const value = c.promised_shows_update !== undefined ? c.promised_shows_update : (c.promised_shows || 0);
        return total + value;
      }, 0);
    
    // The minimum for this record is: minSpotsToKeepForDisplay - otherContentsTotal
    // But it can't be negative
    const minForThisRecord = Math.max(0, minSpotsToKeepForDisplay - otherContentsTotal);
    
    return minForThisRecord;
  };

// Calculate the impressions added and subtracted for displays
 const calculateDiffValue = (type, content_display) => {
    if (content_display.promised_shows_update == undefined){
      return 0
    }
    if(type === "add"){
      let aux = content_display.promised_shows_update - content_display.promised_shows_origin
      if(aux > 0){
        return aux
      }
    }
    if(type === "subtract"){
      let aux = content_display.promised_shows_origin - content_display.promised_shows_update
      if(aux > 0){
        return aux
      }
    }
    return 0
  }

  const messageStatus = (item) => {
    if (item.subsidized_price) {
      if (item.promised_shows !== 0) {
        return (
          <span className="ant-tag ant-tag-orange" style={{ marginLeft: "5px", fontSize: "10px" }}>
            {t('Subsidized')}
          </span>
        );
      } else {
        // When the user changes the value to zero.
        return (
          <span className="ant-tag ant-tag-blue" style={{ marginLeft: "5px", fontSize: "10px" }}>
            {t('Suspended')}
          </span>
        );
      }
    }
    if ((item.promised_shows_origin !== item.promised_shows_update ? item.promised_shows_update : calculateImpressionsView(item)) === 0) {
      return (
        <span style={{ color: Colors.red, marginLeft: "5px", fontSize: "12px" }}>
          {t("It will be in suspended state if the value is 0.")}
        </span>
      );
    }
  }

  const limitAddImpressions = (item) => {
    // Convert smart_campaign_cpm (1 impre = 0.001 cpm) to cpm (1 impre = 1 cpm)
    let smart_campaign_cpm_per_impre = item.smart_campaign_cpm / 1000;
    let limit = parseFloat((creditAvailable / smart_campaign_cpm_per_impre).toFixed(DECIMAL_FOR_CHECK));
    if ( limit < 1 || creditAvailable === 0) {
      return  item.promised_shows_update || item.promised_shows_update == 0 ? item.promised_shows_update : calculateImpressionsView(item)
    }
    return item.promised_shows_origin && (item.promised_shows_origin != item.promised_shows_update) ?
          (item.promised_shows_update) + limit: (calculateImpressionsView(item) + limit)
  }

 //Update CustomContents with contentsDisplaysSelect
 const updateImpressionsCustomContents = () => {
   contentsDisplaysSelect.forEach(group => {
     group.customContents.forEach(item => {
       // promised_shows_update only exists if the user has modified the value
       if (item.promised_shows_update != undefined) {
         let add_max_in_transfer = calculateDiffValue( "add", item )
         let promisedInSingleData = reportSingleData.report ? reportSingleData.report.find(element => element.display === item.displayId && element.content === item.content.id) : null
         let impression_update = item.promised_shows_update + (promisedInSingleData ? promisedInSingleData.shows : 0 )
         updateImpressionsDisplay(item.displayId, item.relationId, impression_update, item.promised_shows_update, add_max_in_transfer);
       } else {
         updateImpressionsDisplay(item.displayId, item.relationId, item.promised_shows, 0 );
       }
     });
   });

   showTransferModal();
  }

  // Return a tag of status with tooltip and color according to the business logic
  const getSpotStatusTag = (item, t) => {
    // Search revision_status in currentCampaign.contents_displays equal to item.relationId
    const contentDisplay = currentCampaign.contents_displays.find(
      cd =>
        cd.id === item.relationId &&
        cd.display_id === item.displayId &&
        cd.content_id === item.content.id
    );

    if (!contentDisplay) return null;

    const revisionStatus = contentDisplay.revision_status;
    const isActive = item.active;
    const pendingContent = false;

    if (revisionStatus === "approved" && !pendingContent && isActive === 1) {
      return (
        <Tooltip title={t('Spot Approved')}>
          <Badge color="green" />
        </Tooltip>
      );
    }
    if (isActive === 0) {
      return (
        <Tooltip title={t('Paused')}>
          <Badge color="yellow" />
        </Tooltip>
      );
    }
    if (revisionStatus === "rejected" && !pendingContent) {
      return (
        <Tooltip title={t('Spot Rejected')}>
          <Badge color="red" />
        </Tooltip>
      );
    }
    if (revisionStatus === "pending" || pendingContent) {
      return (
        <Tooltip title={t('The owner of this screen has not yet approved')}>
          <Badge color="orange" />
        </Tooltip>
      );
    }
    if (revisionStatus === "out_of_service") {
      return (
        <Tooltip title={t('Display out of service')}>
          <Badge color="red" />
        </Tooltip>
      );
    }
    return null;
  };

  return (
      <ImpressionManagementHolder>
        <Row>
          <Col
              xs={12} sm={12} md={12} lg={12} xl={12}
            >
              <div className='row-info'>
                <p style={{marginRight: "10px", paddingTop: "5px"}}>{t('Available credit')}</p>
                <InputNumber
                  disabled
                  min={0}
                  value={Math.max(0, creditAvailable)}
                  formatter={value =>
                    value === 0 ? format.format(0) : format.format(value)
                  }
                  style={{width:"150px"}}
                  className='text-impression-disabled-important'
                  />
              </div>
          </Col>
          <Col
              xs={12} sm={12} md={12} lg={12} xl={12}
            >
              <div className='row-info' style={{justifyContent: "flex-end"}}>
                <Radio.Group
                  onChange={handleRadioChange}
                  value={view}
                  buttonStyle='solid'
                  defaultValue="ad"
                >
                  <Radio.Button value="ad">{t('By Ad')}</Radio.Button>
                  <Radio.Button value="display">{t('By Display')}</Radio.Button>
                </Radio.Group>
              </div>
          </Col>
        </Row>
        
        <Row gutter={8}>
          <Col xs={10} sm={10} md={10} lg={10} xl={10}>
            <div className='row-info'>
              <p style={{marginRight: "10px", paddingTop: "5px"}}>{t('Filter displays')}</p>
              <Select
                mode="multiple"
                style={{ flex: 1 }}
                placeholder={t("Filter displays")}
                defaultValue={["all"]}
                value={displayFilter}
                filterOption={false}
                onSearch={handleSearchDisplayFilter}
                onChange={handleChangeDisplayFilter}
              >
                <Select.Option key="all" value="all">{t("All displays")}</Select.Option>
                {
                  displayOptions.map((item, index) => (
                    <Select.Option key={index} value={item.id}>
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '300px', display: 'block' }}>
                        {item.name}
                      </span>
                    </Select.Option>
                  ))
                }
              </Select>
            </div>
          </Col>
          <Col xs={14} sm={14} md={14} lg={14} xl={14}>
            <div className='row-info' style={{ alignItems: 'center', justifyContent: 'space-between' }}>
              {view === 'ad' ? (
                <div>
                  <span style={{ marginRight: '5px' }}>{t('Transfer the')}</span>
                  <InputNumber
                    min={0}
                    max={100}
                    formatter={value => `${value}%`}
                    parser={value => value.replace('%', '')}
                    style={{ width: '80px', marginRight: '5px' }}
                    value={transferPercentage}
                    onChange={setTransferPercentage}
                  />
                  <span style={{ marginRight: '5px' }}>{t('of outputs from')}</span>
                  <Select
                    style={{ width: '120px', marginRight: '5px' }}
                    placeholder={t('Origin Version')}
                    onChange={setOriginVersion}
                    value={originVersion}
                    id='origin-version'
                  >
                    {
                      adVersions.map(version => (
                        <Select.Option key={version} value={version}>
                          {version}
                        </Select.Option>
                      ))
                    }
                  </Select>
                  <span style={{ marginRight: '5px' }}>{t('to')}</span>
                  <Select
                    style={{ width: '120px' }}
                    placeholder={t('Destination Version')}
                    onChange={setDestinationVersion}
                    value={destinationVersion}
                    id='destination-version'
                  >
                    {
                      adVersions.map((item, index) => (
                        <Select.Option key={index} value={item}>
                          {item}
                        </Select.Option>
                      ))
                    }
                  </Select>
                </div>
              ) : view === 'display' ? (
                <div>
                  <span style={{ marginRight: '5px' }}>{t('Transfer the')}</span>
                  <InputNumber
                    min={0}
                    max={100}
                    formatter={value => `${value}%`}
                    placeholder='%'
                    parser={value =>  value.replace('%', '')}
                    style={{ width: '80px', marginRight: '5px' }}
                    value={transferPercentage}
                    onChange={setTransferPercentage}
                  />
                  <span style={{ marginRight: '5px' }}>{t('of outputs from')}</span>
                  <Select
                    style={{ width: '120px', marginRight: '5px' }}
                    placeholder={t('Origin Screen')}
                    onChange={setOriginScreen}
                    value={originScreen}
                    id='origin-screen'
                  >
                    {
                      cart.filter(screen => {
                        // Filtrar por destinationScreen y por displayFilter
                        return (displayFilter.includes('all') || displayFilter.includes(screen.id));
                      }).map(display => (
                        <Select.Option key={display.id} value={display.id} title={display.name}>
                          {display.name}
                        </Select.Option>
                      ))
                    }
                  </Select>
                  <span style={{ marginRight: '5px' }}>{t('to')}</span>
                  <Select
                    style={{ width: '120px' }}
                    placeholder={t('Destination Screen')}
                    onChange={setDestinationScreen}
                    value={destinationScreen}
                    id='destination-screen'
                  >
                    {
                      cart.filter(display => {
                        // Filtrar por originScreen y por displayFilter
                        return (displayFilter.includes('all') || displayFilter.includes(display.id));
                      }).map(display => (
                        <Select.Option key={display.id} value={display.id} title={display.name}>
                          {display.name}
                        </Select.Option>
                      ))
                    }
                  </Select>
                </div>
              ) : null}
              <Button
                type="primary"
                onClick={handleTransfer}
                disabled={
                  view === 'ad' ?
                  (!originVersion || !destinationVersion) || (originVersion === destinationVersion)
                  : view === 'display' ?
                  (!originScreen || !destinationScreen) || (originScreen === destinationScreen)
                  : true || transferPercentage === 0
                }
              >
                {t('Apply')}
              </Button>
            </div>
          </Col>
        </Row>


        {/* TABLES */}
        {
          view === "ad" ? (
            <div className="table-container">
              <AdViewComponent
                contentsDisplaysSelect={contentsDisplaysSelect}
                currency={currentCampaign.currency}
                updateImpressions={updateImpressions}
                updateImpressionsToMax={updateImpressionsToMax}
                getSpotStatusTag={getSpotStatusTag}
                messageStatus={messageStatus}
                calculateDiffValue={calculateDiffValue}
                calculateImpressionsView={calculateImpressionsView}
                limitAddImpressions={limitAddImpressions}
                calculateBudget={calculateBudget}
                updateBudget={updateBudget}
                getMinSpotsForRecord={getMinSpotsForRecord}
              />
            </div>
          ) : view === "display" ? (
            <div className="table-container"> 
              <DisplayViewComponent
                contentsDisplaysSelect={contentsDisplaysSelect}
                currency={currentCampaign.currency}
                calculateDiffValue={calculateDiffValue}
                calculateImpressionsView={calculateImpressionsView}
              />
            </div>
          ) : null
        }


        <Row>
          <Col
            className="action-button"
            style={{marginBottom: "0px"}}
            type="flex"
            align="end"
            xs={24} sm={24} md={24} lg={24} xl={24}>
            { creditAvailable > 0 &&
              <p style={{marginBottom: "0px", fontSize: "10px", color: Colors.atention}}>
                *{ t(checkCreditAvailable() ?
                 "You have available credit. You must use it to continue distributing.":
                 "Consumed credit. You can apply changes and edit. The remaining credit (n_credite) cannot be used to purchase 1 spot." ).replace(
                      "n_credite",
                      format.format(creditAvailable)
                )}
              </p>
            }
          </Col>
          <Col
            className="action-button"
            type="flex"
            align="end"
            xs={24} sm={24} md={24} lg={24} xl={24}>
            <Button key="Cancel" onClick={showTransferModal}>
              {t('Cancel')}
            </Button>
            <Button
              key="Apply"
              type='primary'
              onClick={updateImpressionsCustomContents}
              disabled={checkCreditAvailable()}>
              {t('Apply changes')}
            </Button>
          </Col>
        </Row>
      </ImpressionManagementHolder>
  )
}

export default ImpressionManagementComponent;